import {Container} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';

import {PayloadLexical} from 'UI/components/layout/PayloadLexical';

import type {TextType} from 'types/payload-types';

/**
 * The props for the `Text` component, which renders a block of rich text content.
 * This interface defines the expected content structure as well as an optional
 * test identifier for end-to-end testing. The content is rendered using the
 * `PayloadLexical` component for rich text formatting.
 */
interface ComponentProps {
    /**
     * The rich text content to be displayed inside the text block.
     * If omitted, the component will render an empty container.
     */
    content: TextType['content'];
    /**
     * An optional flag to hide the entire block when set to true.
     * When `true`, the component renders nothing.
     *
     * @default false
     */
    hideBlock?: TextType['hideBlock'];
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Text'
     */
    testId?: string;
}

/**
 * A simple text block component that renders rich text content inside a styled container.
 * This component displays content provided as `LexicalContent` using the `PayloadLexical`
 * renderer. If no content is provided, the component renders an empty container.
 * A `data-cy` attribute is applied to the container for end-to-end test targeting.
 *
 * @param props           The component props.
 * @param props.content   The rich text content to display. If omitted, nothing is rendered inside the container.
 * @param props.hideBlock An optional flag to hide the entire block when set to true. Defaults to `false`.
 * @param props.testId    An optional identifier used for testing, applied as `data-cy`. Defaults to `'Text'`.
 * @returns A JSX element rendering the text block.
 *
 * @example
 * ```tsx
 * <Text content={richTextContent} />
 * ```
 */
const Text = observer(({content, hideBlock = false, testId = 'Text'}: ComponentProps) => (hideBlock ? null : (
    <Container data-cy={testId}>
        {content && <PayloadLexical description={content} />}
    </Container>
)));

Text.displayName = 'Text';

export {Text};